import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Bell, UserPlus, CalendarCheck, ChatCircleDots, PushPin, Check, Detective, EnvelopeSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICONS = {
  connection_request: UserPlus,
  connection_accepted: Check,
  event_rsvp: CalendarCheck,
  new_message: ChatCircleDots,
  storyboard: PushPin,
  find_response: Detective,
  find_message: EnvelopeSimple,
  find_comment: ChatCircleDots,
};

export default function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);

  const refresh = useCallback(() => {
    api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  const open = (o) => {
    if (o) api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});
  };

  const markAll = async () => { await api.post("/notifications/read-all"); setCount(0); refresh(); };

  const go = async (n) => {
    await api.put(`/notifications/${n.id}/read`).catch(() => {});
    refresh();
    if (n.link) nav(n.link);
  };

  return (
    <DropdownMenu onOpenChange={open}>
      <DropdownMenuTrigger asChild>
        <button className="relative grid h-10 w-10 place-items-center rounded-full border border-border/60 hover:bg-accent/60 transition-colors" data-testid="notification-bell">
          <Bell size={19} weight={count > 0 ? "fill" : "regular"} />
          {count > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-[20px] place-items-center rounded-full bg-secondary px-1 text-[10px] font-semibold text-secondary-foreground" data-testid="notification-count">{count > 9 ? "9+" : count}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <span className="font-heading font-medium">Notifications</span>
          {items.some((i) => !i.read) && <button onClick={markAll} className="text-xs text-secondary hover:underline" data-testid="mark-all-read">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground">You're all caught up 🎉</p>}
          {items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const initials = (n.actor_name || "").split(" ").map((s) => s[0]).slice(0, 2).join("");
            return (
              <button key={n.id} onClick={() => go(n)} className={`flex w-full items-start gap-3 border-b border-border/40 px-4 py-3 text-left transition-colors hover:bg-accent/50 ${n.read ? "" : "bg-secondary/5"}`} data-testid="notification-item">
                <div className="relative">
                  <Avatar className="h-9 w-9"><AvatarImage src={fileUrl(n.actor_avatar)} /><AvatarFallback className="bg-accent text-xs">{initials || "•"}</AvatarFallback></Avatar>
                  <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-secondary text-secondary-foreground"><Icon size={11} weight="fill" /></span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm leading-snug">{n.text}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-secondary" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
